// utils
import { format } from 'date-fns';

function _findSchedulePending(scheduleReport) {
  const detailedPendings = buildPendingReportMsg(scheduleReport);
  const summaryPendings = buildGroupPedingSchedules(scheduleReport);

  return { detailedPendings, summaryPendings };
}

export { _findSchedulePending };

/**
 * Gera um array de mensagens, onde cada mensagem descreve a insuficiência de
 * agendamento para um dia específico que não atingiu a meta de visitas.
 *
 * @param {Array<Object>} scheduleReport - O array de objetos contendo os agendamentos por dia.
 * @returns {Array<string>} Um array de strings formatadas com a contagem de visitas faltantes e a data.
 */
const buildPendingReportMsg = scheduleReport => {
  const hasInsufficientSchedule = sReport => sReport.totalScheduled < sReport.totalRequired;

  const formatMessage = sReport => {
    const { date, totalRequired, totalScheduled } = sReport;
    const formatedDate = format(date, 'DD/MM/YYYY');
    return `Falta(m) ${totalRequired - totalScheduled} visita(s) para o dia ${formatedDate}`;
  };

  /**
   * Fluxo principal:
   * 1. Filtra (filter): Usa hasInsufficientSchedule para manter apenas os dias incompletos.
   * 2. Mapeia (map): Usa formatMessage para transformar cada relatório incompleto
   * em uma string de mensagem legível.
   */
  return scheduleReport.filter(hasInsufficientSchedule).map(formatMessage);
};

/**
 * Agrupa os agendamentos por quantos itens estão faltando
 * (totalRequired - totalScheduled) e gera um array de mensagens concisas.
 * * @param {Array<Object>} scheduleReport - O array de agendamento.
 * @returns {Array<string>} Array de strings formatadas com as datas agrupadas.
 */
const buildGroupPedingSchedules = scheduleReport => {
  // 1. Filtra e Agrupa os agendamentos por visitas faltantes
  const groupedSchedules = scheduleReport.reduce((acc, report) => {
    const missingCount = report.totalRequired - report.totalScheduled;

    // Apenas considera agendamento insuficiente
    if (missingCount > 0) {
      // Usa o número de faltas (missingCount) como chave de agrupamento
      const key = missingCount.toString();

      if (!acc[key]) {
        acc[key] = [];
      }
      // Adiciona o agendamento à lista do seu grupo
      acc[key].push(report);
    }
    return acc;
  }, {});

  // 2. Mapeia os grupos para o formato de mensagem final
  const pendingGroupedSchedule = Object.entries(groupedSchedules).map(
    ([missingCountStr, reports]) => {
      const missingCount = parseInt(missingCountStr, 10);

      // Extrai e formata todas as datas do grupo
      const formattedDates = reports.map(r => format(r.date, 'DD/MM/YYYY'));

      // Formata a string das datas, usando vírgulas e "e" para a última data.
      let datesString;
      if (formattedDates.length === 1) {
        datesString = formattedDates[0];
      } else {
        // Separa a última data para usar "e" (and)
        const lastDate = formattedDates.pop();
        datesString = `${formattedDates.join(', ')} e ${lastDate}`;
      }

      // Determina plural/singular
      const pluralFalta = missingCount === 1 ? 'Falta' : 'Faltam';
      const pluralVisit = missingCount === 1 ? 'visita' : 'visitas';
      const pluralDay = reports.length === 1 ? 'o dia' : 'os dias';

      // Constrói a mensagem final
      return `${pluralFalta} ${missingCount} ${pluralVisit} para ${pluralDay} ${datesString}`;
    }
  );

  return pendingGroupedSchedule;
};
